/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
/* eslint-disable max-len */

import React, { ReactElement, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@userzoomlibraries/toolkit-button';
import StudyLayout from '@userzoomplatform/pe-study-layout';
import { gql, useQuery } from '@apollo/client';
import { UaParserDom, Browsers } from '@userzoomplatform/ua-parser';

import { useWebExtension } from '../../../../providers/web-extension';
import { ExtensionEvents, ExtensionStatus } from '../../../../utils/constants';
import { Namespaces } from '../../../../utils/i18n';
import { usePageView, useToken } from '../../../../hooks';
import config from '../../../../utils/config';

type Props = {
	className?: string;
	moveNext?: () => void;
};

const defaultProps = {
	className: '',
	moveNext: () => null,
};

const extensionQuery = gql`
	query webExtension($browser: String, $beta: Boolean) {
		participant {
			userCode
		}
		webExtension {
			installation(browser: $browser, beta: $beta) {
				installationUrl
			}
		}
	}
`;

const InstallExtension = ({ className, moveNext }: Props): ReactElement => {
	const { t } = useTranslation([Namespaces.Common]);
	const { state } = useWebExtension();
	const token = useToken();
	usePageView('/install-extension');

	const { apiUrl, splitIOApiKey, server3Url, gaCode, env, assetsBasePath } = config();

	const uaParser = new UaParserDom(window.navigator.userAgent);
	const { browser } = uaParser.getDevice();
	const {
		error,
		loading,
		data: { webExtension, participant } = {},
	} = useQuery(extensionQuery, { variables: { browser: browser?.name, beta: state.useBeta } });

	// startStudy
	useEffect(() => {
		if (moveNext) {
			moveNext();
		}
	}, [moveNext]);

	useEffect(() => {
		if (!loading && (state.status === ExtensionStatus.Installed || state.useDev)) {
			window.dispatchEvent(
				new CustomEvent(ExtensionEvents.RedirectToExtension, {
					detail: {
						token,
						splitIO: splitIOApiKey,
						apiUrl,
						server3Url,
						uc: participant.userCode,
						ga: gaCode,
						env,
						assetsBasePath,
					},
				})
			);
		}
	}, [state.status, apiUrl, server3Url, splitIOApiKey, token, participant, state.useDev, loading, gaCode, env, assetsBasePath]);

	const isWaitingForExtension =
		([ExtensionStatus.Pending, ExtensionStatus.Outdated].includes(state.status) || !webExtension || error) &&
		browser?.name !== Browsers.Firefox;

	const redirectToChromeInstallation = useCallback(() => {
		window.location.href = `${webExtension.installation.installationUrl}?token=${token}&apiUrl=${apiUrl}&splitIO=${splitIOApiKey}&server3Url=${server3Url}&uc=${participant.userCode}&ga=${gaCode}`;
	}, [token, apiUrl, webExtension, splitIOApiKey, server3Url, participant, gaCode]);

	const installFirefoxExtension = useCallback(() => {
		window.location = webExtension.installation.installationUrl;
	}, [webExtension?.installation.installationUrl]);

	const installExtension = useCallback(() => {
		if (browser?.name === Browsers.Chrome || browser?.name === Browsers.Edge) {
			redirectToChromeInstallation();
		} else if (browser?.name === Browsers.Firefox) {
			installFirefoxExtension();
		}
	}, [browser?.name, redirectToChromeInstallation, installFirefoxExtension]);

	if (loading || state.status === ExtensionStatus.Installed || state.useDev) {
		return <div id="redirect-extension" />;
	}

	return (
		<StudyLayout className={className}>
			<StudyLayout.Header />
			<StudyLayout.Content>
				{!isWaitingForExtension ? (
					<div className="content">
						<h2>{t('installExtension.pleaseInstall')}</h2>
						<hr />
						<span>
							{t(
								browser?.name === Browsers.Firefox
									? 'installExtension.whenClickingInstallFirefox'
									: 'installExtension.whenClickingInstallChrome'
							)}
						</span>
						<span>{t('installExtension.onceInstalledRedirected')}</span>
						<Button size="lg" className="cta" onClick={installExtension}>
							{t('installExtension.installUserZoomSurveys')}
						</Button>
						<div className="secured-container">
							<div>
								<img src={`${process.env.PUBLIC_URL}/images/digicert_logo_2.png`} alt="Digicert logo" />
							</div>
							<a
								href="//privacy.truste.com/privacy-seal/validation?rid=b9b894c5-9aa0-42d3-9d85-49ee5f4004c6"
								target="_blank"
								rel="noreferrer"
							>
								<img
									src="//privacy-policy.truste.com/privacy-seal/seal?rid=b9b894c5-9aa0-42d3-9d85-49ee5f4004c6"
									alt="Truste privacy seal"
								/>
							</a>
							<p className="secured-item secured-item-text">{t('installExtension.authenticatedByDigicert')}</p>
						</div>
					</div>
				) : (
					<div className="checking-content">
						<strong>{t('installExtension.checkingExtension')}</strong>
						<img className="loading-image" src={`${process.env.PUBLIC_URL}/images/animated/checking-puzzle.svg`} alt="Loading animation" />
					</div>
				)}
			</StudyLayout.Content>
			{!isWaitingForExtension && <StudyLayout.Footer>{null}</StudyLayout.Footer>}
			<StudyLayout.FooterText />
		</StudyLayout>
	);
};

InstallExtension.defaultProps = defaultProps;

export default InstallExtension;
